import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { Translations } from '../i18n/translations';
import './PremiumFeatureModal.css';

// A friendly "here's what you'd get" teaser shown when a free user reaches
// a premium feature — a demo of the value, not a dead end.

export type PremiumFeatureKey = 'ocr' | 'ai_tagging' | 'discovery' | 'import' | 'video_summary'
    | 'collaboration';

interface FeatureCopy {
    emoji: string;
    title: string;
    tagline: string;
    bullets: string[];
    demo?: React.ReactNode;
}

// A small static sample graph so the Discovery teaser shows the idea
const DiscoveryDemo: React.FC = () => (
    <svg viewBox="0 0 240 130" className="premium-demo-svg" aria-hidden="true">
        <line x1="120" y1="65" x2="55" y2="35" stroke="#5b94f1" strokeWidth="2" opacity="0.6" />
        <line x1="120" y1="65" x2="60" y2="100" stroke="#ea6e6e" strokeWidth="2" opacity="0.6" />
        <line x1="120" y1="65" x2="185" y2="40" stroke="#5becbb" strokeWidth="2" opacity="0.6" />
        <line x1="120" y1="65" x2="180" y2="100" stroke="#efbc65" strokeWidth="2" opacity="0.6" />
        <line x1="185" y1="40" x2="180" y2="100" stroke="#b497f6" strokeWidth="1.5" opacity="0.4" />
        <circle cx="120" cy="65" r="16" fill="#5b94f1" />
        <circle cx="55" cy="35" r="10" fill="#ea6e6e" />
        <circle cx="60" cy="100" r="10" fill="#5becbb" />
        <circle cx="185" cy="40" r="10" fill="#efbc65" />
        <circle cx="180" cy="100" r="10" fill="#b497f6" />
    </svg>
);

// The wording lives in translations.ts; only the emoji and the demo graphic
// are fixed here.
const buildCopy = (t: Translations): Record<PremiumFeatureKey, FeatureCopy> => ({
    ocr: {
        emoji: '🔎',
        title: t.premium.ocrTitle,
        tagline: t.premium.ocrTagline,
        bullets: [t.premium.ocrBullet1, t.premium.ocrBullet2, t.premium.ocrBullet3],
    },
    ai_tagging: {
        emoji: '🏷️',
        title: t.premium.aiTaggingTitle,
        tagline: t.premium.aiTaggingTagline,
        bullets: [t.premium.aiTaggingBullet1, t.premium.aiTaggingBullet2, t.premium.aiTaggingBullet3],
    },
    discovery: {
        emoji: '🕸️',
        title: t.premium.discoveryTitle,
        tagline: t.premium.discoveryTagline,
        bullets: [t.premium.discoveryBullet1, t.premium.discoveryBullet2, t.premium.discoveryBullet3],
        demo: <DiscoveryDemo />,
    },
    video_summary: {
        emoji: '📹',
        title: t.premium.videoSummaryTitle,
        tagline: t.premium.videoSummaryTagline,
        bullets: [t.premium.videoSummaryBullet1, t.premium.videoSummaryBullet2, t.premium.videoSummaryBullet3],
    },
    collaboration: {
        emoji: '🤝',
        title: t.premium.collaborationTitle,
        tagline: t.premium.collaborationTagline,
        bullets: [t.premium.collaborationBullet1, t.premium.collaborationBullet2, t.premium.collaborationBullet3],
    },
    import: {
        emoji: '📥',
        title: t.premium.importTitle,
        tagline: t.premium.importTagline,
        bullets: [t.premium.importBullet1, t.premium.importBullet2, t.premium.importBullet3],
    },
});

interface PremiumFeatureModalProps {
    feature: PremiumFeatureKey;
    onClose: () => void;
}

export const PremiumFeatureModal: React.FC<PremiumFeatureModalProps> = ({ feature, onClose }) => {
    const navigate = useNavigate();
    const { t } = useLanguage();
    const copy = buildCopy(t)[feature];

    return (
        <div className="premium-overlay" onClick={onClose}>
            <div className="premium-dialog" onClick={(e) => e.stopPropagation()}>
                <button className="premium-close" aria-label={t.common.close} onClick={onClose}>✕</button>
                <div className="premium-emoji">{copy.emoji}</div>
                <h3 className="premium-title">{copy.title}</h3>
                <p className="premium-tagline">{copy.tagline}</p>
                {copy.demo && <div className="premium-demo">{copy.demo}</div>}
                <ul className="premium-bullets">
                    {copy.bullets.map((b, i) => (
                        <li key={i}>✓ {b}</li>
                    ))}
                </ul>
                <div className="premium-badge-row">
                    <span className="premium-lock">🔒 {t.premium.premiumFeature}</span>
                </div>
                <button
                    className="premium-upgrade-btn"
                    onClick={() => { onClose(); navigate('/pricing'); }}
                >
                    {t.premium.seePlans}
                </button>
                <button className="premium-later-btn" onClick={onClose}>{t.premium.maybeLater}</button>
            </div>
        </div>
    );
};

export default PremiumFeatureModal;
